/**
 * MOB-04 — iPhone / PWA QR save via Web Share API (files).
 * `<a download>` on blob/data URLs fails in iOS PWA; share-or-preview is the primary path.
 */

import { qrLog } from './logging.js';

/** True when the runtime can share this PNG file via `navigator.share({ files })`. */
export function canSharePaymentQrFile(file: File): boolean {
  if (typeof navigator === 'undefined' || typeof navigator.share !== 'function') {
    return false;
  }
  if (typeof navigator.canShare !== 'function') {
    return false;
  }
  try {
    return navigator.canShare({ files: [file] });
  } catch (error: unknown) {
    qrLog.warn('canShare({ files }) check failed', {
      operation: 'canSharePaymentQrFile',
      reason: error instanceof Error ? error.name : 'unknown',
    });
    return false;
  }
}

/**
 * True when programmatic `<a download>` blob saves are expected to work.
 *
 * Prefer feature signals over UA:
 * 1. Missing `HTMLAnchorElement.download` support → false.
 * 2. iOS / iPadOS still expose `download` but blob/object-URL clicks often silent
 *    no-op in Safari and especially standalone PWA — there is no reliable Web API
 *    for that failure mode, so a narrow platform check documents the carve-out.
 */
export function canTriggerPngBlobDownload(): boolean {
  if (typeof document === 'undefined') {
    return false;
  }
  const anchor = document.createElement('a');
  if (typeof anchor.download !== 'string') {
    return false;
  }
  if (isIosLikeBlobDownloadUnreliable()) {
    return false;
  }
  return true;
}

/**
 * iOS/iPadOS: `download` attribute exists but blob downloads frequently no-op.
 * iPadOS 13+ may report desktop Mac UA — maxTouchPoints distinguishes it.
 */
function isIosLikeBlobDownloadUnreliable(): boolean {
  if (typeof navigator === 'undefined') {
    return false;
  }
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/i.test(ua)) {
    return true;
  }
  return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
}

export function isShareAbortError(error: unknown): boolean {
  if (error instanceof DOMException && error.name === 'AbortError') {
    return true;
  }
  return error instanceof Error && error.name === 'AbortError';
}

/**
 * Convert a `data:` URL (e.g. QRCode.toDataURL) into a PNG Blob.
 * Decodes in-process — never `fetch(data:…)` (CSP `connect-src` blocks it in production).
 */
export async function dataUrlToPngBlob(dataUrl: string): Promise<Blob> {
  const trimmed = dataUrl.trim();
  const match =
    /^data:([^;,]+)?(?:;charset=[^;,]+)?(;base64)?,([\s\S]*)$/i.exec(trimmed);
  if (match === null) {
    throw new Error('data URL parse failed: not a data: URL');
  }
  const mimeRaw = (match[1] ?? 'image/png').trim().toLowerCase();
  const mime = mimeRaw.length > 0 ? mimeRaw : 'image/png';
  if (!mime.startsWith('image/')) {
    throw new Error(`unexpected data URL blob type: ${mime}`);
  }
  const isBase64 = match[2] !== undefined && match[2].length > 0;
  const payload = match[3] ?? '';
  try {
    if (isBase64) {
      const binary = atob(payload);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i += 1) {
        bytes[i] = binary.charCodeAt(i);
      }
      return new Blob([bytes], { type: mime === 'image/png' ? 'image/png' : mime });
    }
    const decoded = decodeURIComponent(payload);
    return new Blob([decoded], { type: mime === 'image/png' ? 'image/png' : mime });
  } catch {
    // Do not embed decoder/DOM detail — callers map failures to i18n UI copy.
    throw new Error('data URL decode failed');
  }
}

export function pngBlobToFile(blob: Blob, filename: string): File {
  return new File([blob], filename, {
    type: blob.type.length > 0 ? blob.type : 'image/png',
  });
}

/**
 * Programmatic desktop download — same pattern as `triggerBlobDownload` in
 * accountReceiptDownload (create → click → revoke). Never navigates the tab.
 * Callers should gate with `canTriggerPngBlobDownload()` (G12).
 */
export function triggerPngBlobDownload(blob: Blob, filename: string): void {
  const objectUrl = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

export type SharePaymentQrResult = 'shared' | 'cancelled';

export async function sharePaymentQrFile(input: {
  file: File;
  title: string;
}): Promise<SharePaymentQrResult> {
  try {
    await navigator.share({
      files: [input.file],
      title: input.title,
    });
    return 'shared';
  } catch (error: unknown) {
    if (isShareAbortError(error)) {
      return 'cancelled';
    }
    // Generic throw only — QrPaymentView shows qrPayment.technicalError, never err.message.
    throw new Error('navigator.share failed');
  }
}
